"use client"

import { User, Lock, Eye, EyeOff, Loader2, AlertCircle, UserPlus } from "lucide-react"
import { cn } from "@/lib/utils"
import type { AuthFormState } from "@/lib/hooks/use-auth-form"

interface LoginTabAccountProps {
  username: string
  setUsername: (v: string) => void
  password: string
  setPassword: (v: string) => void
  showPassword: boolean
  setShowPassword: (v: boolean) => void
  form: AuthFormState
  mode: "login" | "register"
  onSubmit: (e: React.FormEvent) => void
}

/**
 * LoginTabAccount — username + password form used by both login and register (username tab).
 */
export function LoginTabAccount({
  username,
  setUsername,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  form,
  mode,
  onSubmit,
}: LoginTabAccountProps) {
  const { loading, error } = form

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Username */}
      <div className="space-y-2">
        <label
          htmlFor={mode === "login" ? "login-username" : "reg-username"}
          className="text-xs font-medium text-muted-foreground pl-1 tracking-wide"
        >
          {mode === "register" ? "手机号码" : "用户名"}
        </label>
        <div className="relative">
          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            id={mode === "login" ? "login-username" : "reg-username"}
            type="text"
            value={username}
            onChange={(e) => { setUsername(e.target.value); form.setError("") }}
            placeholder={mode === "register" ? "输入手机号码" : "输入用户名"}
            autoComplete="username"
            className={cn(
              "w-full h-10 pl-10 pr-4 rounded-xl border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all",
              error ? "border-destructive/50 focus:border-destructive/50" : "border-border focus:border-primary/50"
            )}
          />
        </div>
      </div>

      {/* Password */}
      <div className="space-y-2">
        <label
          htmlFor={mode === "login" ? "login-password" : "reg-password"}
          className="text-xs font-medium text-muted-foreground pl-1 tracking-wide"
        >
          密码
        </label>
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            id={mode === "login" ? "login-password" : "reg-password"}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => { setPassword(e.target.value); form.setError("") }}
            placeholder={mode === "register" ? "设置密码" : "输入密码"}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            className={cn(
              "w-full h-10 pl-10 pr-10 rounded-xl border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all",
              error ? "border-destructive/50 focus:border-destructive/50" : "border-border focus:border-primary/50"
            )}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading || !username.trim() || !password.trim()}
        className="w-full h-10 flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-70 transition-colors mt-1"
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 animate-spin" />{mode === "login" ? "登录中..." : "注册中..."}</>
        ) : mode === "login" ? (
          <>登录</>
        ) : (
          <><UserPlus className="h-4 w-4" />注册</>
        )}
      </button>
    </form>
  )
}
